import React, { createContext, useContext, useReducer, useCallback, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { showFlash } from "./flash";

const WMContext = createContext(null);

// Cascade slots — when many windows open, cycle through 6 positions instead of stacking straight off-screen.
const CASCADE_SLOTS = [
  { x: 90, y: 60 },
  { x: 140, y: 90 },
  { x: 190, y: 120 },
  { x: 240, y: 150 },
  { x: 290, y: 180 },
  { x: 340, y: 210 },
];

const initial = {
  windows: [],
  zCounter: 100,
  focused: null,
};

function clampPos(x, y, w, h) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1920;
  const vh = typeof window !== "undefined" ? window.innerHeight : 1000;
  return {
    x: Math.max(64, Math.min(x, vw - 200)),
    y: Math.max(32, Math.min(y, vh - 100)),
    w: Math.min(w, vw - 80),
    h: Math.min(h, vh - 80),
  };
}

function tileLayout(n) {
  // Returns grid: {cols, rows}
  if (n <= 1) return { cols: 1, rows: 1 };
  if (n === 2) return { cols: 2, rows: 1 };
  if (n <= 4) return { cols: 2, rows: 2 };
  if (n <= 6) return { cols: 3, rows: 2 };
  if (n <= 9) return { cols: 3, rows: 3 };
  return { cols: 4, rows: Math.ceil(n / 4) };
}

function computeTiledRect(idx, n) {
  const { cols, rows } = tileLayout(n);
  const vw = typeof window !== "undefined" ? window.innerWidth : 1920;
  const vh = typeof window !== "undefined" ? window.innerHeight : 1000;
  const offsetLeft = 64;  // hex rail width
  const offsetTop = 28;   // topbar height
  const gap = 8;
  const availW = vw - offsetLeft - gap;
  const availH = vh - offsetTop - gap;
  const cellW = Math.floor(availW / cols) - gap;
  const cellH = Math.floor(availH / rows) - gap;
  const col = idx % cols;
  const row = Math.floor(idx / cols);
  return {
    x: offsetLeft + col * (cellW + gap) + gap,
    y: offsetTop + row * (cellH + gap) + gap,
    width: cellW,
    height: cellH,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case "OPEN": {
      const { app, props } = action;
      // If already open and supports single instance, focus + un-minimize + re-maximize
      const existing = state.windows.find(w => w.appId === app.id && !app.allowMultiple);
      if (existing) {
        return {
          ...state,
          zCounter: state.zCounter + 1,
          focused: existing.id,
          windows: state.windows.map(w =>
            w.id === existing.id
              ? { ...w, isMinimized: false, isMaximized: true, zIndex: state.zCounter + 1, props: props || w.props }
              : w
          ),
        };
      }
      const id = `win-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      const baseW = app.defaultSize?.width || 880;
      const baseH = app.defaultSize?.height || 560;
      const visibleCount = state.windows.filter(w => !w.isMinimized).length;

      // Always open apps MAXIMIZED (full-screen) — users can restore/resize afterwards.
      // We still compute a sensible restore-position for when they un-maximize.
      const slot = CASCADE_SLOTS[visibleCount % CASCADE_SLOTS.length];
      const clamped = clampPos(slot.x, slot.y, baseW, baseH);
      const win = {
        id, appId: app.id, title: app.title, Component: app.Component, props: props || {},
        x: clamped.x, y: clamped.y, width: clamped.w, height: clamped.h,
        isMinimized: false, isMaximized: true, zIndex: state.zCounter + 1,
      };
      return {
        ...state,
        zCounter: state.zCounter + 1,
        focused: id,
        windows: [...state.windows, win],
      };
    }
    case "CLOSE": {
      return {
        ...state,
        windows: state.windows.filter(w => w.id !== action.id),
        focused: state.focused === action.id ? null : state.focused,
      };
    }
    case "FOCUS": {
      const z = state.zCounter + 1;
      return {
        ...state,
        zCounter: z,
        focused: action.id,
        windows: state.windows.map(w =>
          w.id === action.id ? { ...w, zIndex: z, isMinimized: false } : w
        ),
      };
    }
    case "MIN": {
      return {
        ...state,
        windows: state.windows.map(w =>
          w.id === action.id ? { ...w, isMinimized: true } : w
        ),
      };
    }
    case "MAX": {
      return {
        ...state,
        windows: state.windows.map(w =>
          w.id === action.id ? { ...w, isMaximized: !w.isMaximized } : w
        ),
      };
    }
    case "MOVE": {
      return {
        ...state,
        windows: state.windows.map(w =>
          w.id === action.id ? { ...w, x: action.x, y: action.y } : w
        ),
      };
    }
    case "RESIZE": {
      return {
        ...state,
        windows: state.windows.map(w =>
          w.id === action.id ? { ...w, width: action.width, height: action.height } : w
        ),
      };
    }
    case "TILE_ALL": {
      // Tile every non-minimized window into a grid
      const visible = state.windows.filter(w => !w.isMinimized);
      const rectMap = {};
      visible.forEach((w, i) => { rectMap[w.id] = computeTiledRect(i, visible.length); });
      return {
        ...state,
        windows: state.windows.map(w =>
          w.isMinimized ? w : { ...w, ...rectMap[w.id], isMaximized: false }
        ),
      };
    }
    case "CASCADE_ALL": {
      const visible = state.windows.filter(w => !w.isMinimized);
      return {
        ...state,
        windows: state.windows.map(w => {
          if (w.isMinimized) return w;
          const i = visible.findIndex(v => v.id === w.id);
          const slot = CASCADE_SLOTS[i % CASCADE_SLOTS.length];
          return { ...w, x: slot.x, y: slot.y, width: 900, height: 600, isMaximized: false };
        }),
      };
    }
    case "MIN_ALL": {
      return { ...state, windows: state.windows.map(w => ({ ...w, isMinimized: true })) };
    }
    case "RESTORE_ALL": {
      const z = state.zCounter + 1;
      return {
        ...state,
        zCounter: z,
        windows: state.windows.map(w => ({ ...w, isMinimized: false })),
      };
    }
    case "MIN_OTHERS": {
      return {
        ...state,
        windows: state.windows.map(w =>
          w.id === action.id ? { ...w, isMinimized: false } : { ...w, isMinimized: true }
        ),
        focused: action.id,
      };
    }
    case "CLOSE_ALL": {
      return { ...state, windows: [], focused: null };
    }
    case "RESTORE_SESSION": {
      // Re-hydrate the windows that were open before a page refresh.
      let z = state.zCounter;
      const windows = action.items.map((it) => {
        z += 1;
        return {
          id: `win-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
          appId: it.app.id, title: it.app.title, Component: it.app.Component, props: it.props || {},
          x: it.x, y: it.y, width: it.width, height: it.height,
          isMinimized: !!it.isMinimized, isMaximized: it.isMaximized !== false, zIndex: z,
        };
      });
      const focusedIdx = action.items.findIndex((it) => it.focused);
      const focused = windows[focusedIdx >= 0 ? focusedIdx : windows.length - 1]?.id || null;
      return { ...state, zCounter: z, focused, windows: focused ? windows.map((w) => (w.id === focused ? { ...w, zIndex: z + 1 } : w)) : windows };
    }
    default:
      return state;
  }
}

export const WM_SESSION_KEY = "hexa_open_windows";

export function WindowManagerProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initial);
  // Flipped to true by markSessionLoaded() once the saved windows have been
  // restored, so the first (empty) render doesn't wipe the saved snapshot.
  const sessionLoaded = React.useRef(false);
  const markSessionLoaded = useCallback(() => { sessionLoaded.current = true; }, []);
  const { appAccess } = useAuth();

  const open = useCallback((app, props) => {
    if (!app) return;
    if (app.redirect) {
      props = { ...app.redirect.props, ...(props || {}) };
      app = app.redirect.app;
    }
    if (appAccess?.hidden?.includes(app.id)) return; // globally disabled — safety net, shouldn't be reachable via UI
    if (appAccess?.override === "restricted" && !(appAccess.enabled_apps || []).includes(app.id)) {
      showFlash(`"${app.title}" is locked — contact HEXA to unlock this app`, { color: "linear-gradient(120deg,#f43f5e,#f97316)" });
      return;
    }
    dispatch({ type: "OPEN", app, props });
  }, [appAccess]);
  const close = useCallback((id) => dispatch({ type: "CLOSE", id }), []);
  const focus = useCallback((id) => dispatch({ type: "FOCUS", id }), []);
  const minimize = useCallback((id) => dispatch({ type: "MIN", id }), []);
  const maximize = useCallback((id) => dispatch({ type: "MAX", id }), []);
  const move = useCallback((id, x, y) => dispatch({ type: "MOVE", id, x, y }), []);
  const resize = useCallback((id, width, height) => dispatch({ type: "RESIZE", id, width, height }), []);
  const tileAll = useCallback(() => dispatch({ type: "TILE_ALL" }), []);
  const cascadeAll = useCallback(() => dispatch({ type: "CASCADE_ALL" }), []);
  const minimizeAll = useCallback(() => dispatch({ type: "MIN_ALL" }), []);
  const restoreAll = useCallback(() => dispatch({ type: "RESTORE_ALL" }), []);
  const minimizeOthers = useCallback((id) => dispatch({ type: "MIN_OTHERS", id }), []);
  const closeAll = useCallback(() => dispatch({ type: "CLOSE_ALL" }), []);
  const restoreSession = useCallback((items) => dispatch({ type: "RESTORE_SESSION", items }), []);

  // Persist the open windows so a browser refresh brings them back instead of
  // dropping the user on the empty desktop. sessionStorage = survives reload,
  // but a brand-new tab still starts clean.
  useEffect(() => {
    if (!sessionLoaded.current) return;
    try {
      const snapshot = state.windows.map((w) => {
        let props = {};
        try { props = JSON.parse(JSON.stringify(w.props || {})); } catch (e) { props = {}; }
        return {
          appId: w.appId, props, x: w.x, y: w.y, width: w.width, height: w.height,
          isMinimized: w.isMinimized, isMaximized: w.isMaximized, focused: w.id === state.focused,
        };
      });
      sessionStorage.setItem(WM_SESSION_KEY, JSON.stringify(snapshot));
    } catch (e) { /* storage blocked — nothing to persist */ }
  }, [state.windows, state.focused]);

  const value = {
    windows: state.windows,
    focused: state.focused,
    open, close, focus, minimize, maximize, move, resize,
    tileAll, cascadeAll, minimizeAll, restoreAll, minimizeOthers, closeAll,
    restoreSession, markSessionLoaded,
  };

  return <WMContext.Provider value={value}>{children}</WMContext.Provider>;
}

export const useWM = () => {
  const ctx = useContext(WMContext);
  if (!ctx) throw new Error("WMContext missing");
  return ctx;
};
